import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useDispatch } from 'react-redux';

import { displayProductTour } from 'features/productTour/productTourSlice';
import { ReactComponent as ForwardArrowIcon } from 'common/assets/forward-arrow-icon.svg';

import { jbColors } from 'app/theme/jbColors';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      position: 'relative',
      marginBottom: theme.spacing(0.5),
      background: `linear-gradient(99.47deg, ${theme.palette.common.white} 0%, #E4E5E6 100.72%)`,
      boxShadow: `0px 3px 5px -1px ${alpha(theme.palette.primary.main, 0.1)}, 0px 1px 18px 0px ${alpha(
        theme.palette.common.black,
        0.12
      )}, 0px 6px 10px 0px ${alpha(theme.palette.primary.main, 0.14)}`,
      borderRadius: 5,
      width: 224,
      '@media (min-width: 360px)': {
        width: 270,
      },
      '@media (min-width: 481px)': {
        width: 360,
      },
    },

    innerContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      textDecoration: 'none',
      color: 'inherit',
      overflow: 'hidden',
      position: 'relative',
      minHeight: 56,
      padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
      '@media (min-width: 360px)': {
        minHeight: 72,
        padding: `${theme.spacing(2.5)} ${theme.spacing(2.5)} ${theme.spacing(2)}`,
      },
      '@media (min-width: 481px)': {
        minHeight: 96,
        padding: `${theme.spacing(3.5)} ${theme.spacing(4)} ${theme.spacing(3)}`,
      },
    },

    title: {
      color: jbColors.support.royalBlue700,
      fontWeight: theme.typography.fontWeightLight,
      fontSize: theme.typography.pxToRem(12),
      lineHeight: 1.6,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    description: {
      marginTop: theme.spacing(0.25),
      fontWeight: theme.typography.fontWeightLight,
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.43,
      letterSpacing: '0.08em',
      '@media (min-width: 360px)': {
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.3,
      },
      '@media (min-width: 481px)': {
        fontSize: theme.typography.pxToRem(16),
        lineHeight: 1.3,
      },
    },
  })
);

export const MigrationProductTourCard = forwardRef<HTMLLIElement, object>((_, ref) => {
  const classes = useStyles();

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    dispatch(displayProductTour());
  };
  return (
    <li className={classes.container} ref={ref}>
      <div className={classes.innerContainer} onClick={handleClick}>
        <Typography component="div" className={classes.title}>
          {t('migration.tile.productTour.title')}
        </Typography>
        <div>
          <Typography variant="h2" component="span" className={classes.description}>
            {t('migration.tile.productTour.description')}
          </Typography>
          <ForwardArrowIcon
            style={{ display: 'inline', height: 16, position: 'relative', top: 3 }}
          />
        </div>
      </div>
    </li>
  );
});
